// PickList — disambiguation sheet for map taps that hit multiple
// features at once (e.g. tapping in a region with airport + nearby fix
// + navaid clustered together). Renders a compact list anchored near
// the tap; tapping a row drills into the normal detail surface for
// that hit.
//
// Anchored absolutely to the tap pixel when given a `point`; falls
// back to bottom-right if no anchor is provided. We clamp to the
// viewport so the sheet never spills off-screen on iPhone.

import { useEffect, useRef } from 'react';
import { SearchHit } from '../lib/search';

interface Props {
  hits: SearchHit[];
  /** Tap point in canvas-relative pixels. */
  point?: { x: number; y: number };
  onPick: (h: SearchHit) => void;
  onClose: () => void;
}

const KIND_DOT: Record<SearchHit['kind'], string> = {
  airport: 'bg-ember-500',
  navaid: 'bg-cyan-400',
  fix: 'bg-violet-400',
};

const KIND_LABEL: Record<SearchHit['kind'], string> = {
  airport: 'APT',
  navaid: 'NAV',
  fix: 'FIX',
};

export default function PickList({ hits, point, onPick, onClose }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);

  // Close on outside click / Escape so the sheet feels modal-ish but
  // not blocking — same affordance as DetailPane.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    function onDocPointer(e: PointerEvent) {
      const node = ref.current;
      if (!node) return;
      if (node.contains(e.target as Node)) return;
      onClose();
    }
    window.addEventListener('keydown', onKey);
    // Defer attach so the same tap that opened the sheet doesn't close it.
    const t = window.setTimeout(() => {
      window.addEventListener('pointerdown', onDocPointer, true);
    }, 0);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onDocPointer, true);
    };
  }, [onClose]);

  // Anchor near the tap. We bias the sheet up-and-right of the tap
  // point so the user's finger doesn't cover the list. Clamped to
  // viewport so it stays on-screen.
  let style: React.CSSProperties;
  if (point) {
    const W = 240;
    const maxH = Math.min(320, hits.length * 56 + 56);
    const margin = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = point.x + 14;
    if (left + W + margin > vw) left = Math.max(margin, point.x - W - 14);
    let top = point.y - 8;
    if (top + maxH + margin > vh) top = Math.max(margin, vh - maxH - margin);
    if (top < margin) top = margin;
    style = { position: 'absolute', left, top, width: W };
  } else {
    style = { position: 'absolute', right: 12, bottom: 12, width: 240 };
  }

  return (
    <div
      ref={ref}
      style={style}
      className="z-30 bg-cockpit-800/95 backdrop-blur border border-cockpit-600 rounded-xl shadow-rail overflow-hidden"
      role="dialog"
      aria-label="Choose a feature"
    >
      <div className="px-3 py-2 border-b border-cockpit-700 flex items-center justify-between">
        <span className="text-[11px] tracking-widest text-cockpit-300">
          {hits.length} ITEMS NEARBY
        </span>
        <button
          onClick={onClose}
          className="text-cockpit-300 hover:text-cockpit-100 text-sm leading-none"
          aria-label="Close"
        >
          ✕
        </button>
      </div>
      <ul className="max-h-72 overflow-y-auto scroll-touch divide-y divide-cockpit-700">
        {hits.map((h) => (
          <li key={`${h.kind}-${h.ident}-${h.lat.toFixed(4)}-${h.lon.toFixed(4)}`}>
            <button
              type="button"
              onClick={() => onPick(h)}
              className="w-full text-left px-3 py-2 hover:bg-cockpit-700 active:bg-cockpit-700 flex items-center gap-2"
            >
              <span
                className={`inline-block h-2.5 w-2.5 rounded-full ${KIND_DOT[h.kind]} shrink-0`}
                aria-hidden
              />
              <span className="flex flex-col min-w-0 flex-1">
                <span className="font-mono text-sm text-cockpit-100 truncate">
                  {h.ident}
                </span>
                <span className="text-[11px] text-cockpit-300 truncate">
                  {h.name}
                  {h.sub ? ` · ${h.sub}` : ''}
                </span>
              </span>
              <span className="text-[10px] tracking-widest text-cockpit-400 shrink-0">
                {KIND_LABEL[h.kind]}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
